import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useState } from "react";
import {
  MapPin, Clock, Users, CalendarIcon, Check, X, AlertTriangle,
  Backpack, Star, Loader2, Minus, Plus, ChevronRight, Compass,
} from "lucide-react";

const TourDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [excursionDate, setExcursionDate] = useState<Date>();
  const [selectedSchedule, setSelectedSchedule] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [activeImage, setActiveImage] = useState(0);

  const { data: tour, isLoading } = useQuery({
    queryKey: ["tour-detail", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tours")
        .select("*, destinations(title, slug)")
        .eq("slug", slug!)
        .eq("is_active", true)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: packages = [] } = useQuery({
    queryKey: ["tour-packages", tour?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tour_packages")
        .select("*")
        .eq("tour_id", tour!.id)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    enabled: !!tour?.id && tour?.pricing_mode === "packages",
  });

  if (isLoading) {
    return (
      <main className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center items-center py-40">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
        </div>
      </main>
    );
  }

  if (!tour) {
    return (
      <main className="min-h-screen bg-background">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-40 gap-4">
          <p className="text-muted-foreground text-lg">Tour not found.</p>
          <Link to="/tours"><Button>Back to Tours</Button></Link>
        </div>
        <Footer />
      </main>
    );
  }

  const highlights = Array.isArray(tour.highlights) ? tour.highlights as string[] : [];
  const includes = Array.isArray(tour.includes) ? tour.includes as string[] : [];
  const notIncludes = Array.isArray(tour.not_includes) ? tour.not_includes as string[] : [];
  const restrictions = Array.isArray(tour.restrictions) ? tour.restrictions as string[] : [];
  const whatToBring = Array.isArray(tour.what_to_bring) ? tour.what_to_bring as string[] : [];
  const schedules = Array.isArray(tour.available_schedules) ? tour.available_schedules as string[] : [];
  const gallery = Array.isArray(tour.gallery) ? tour.gallery as string[] : [];
  const allImages = [tour.main_image_url, ...gallery].filter(Boolean) as string[];

  const isFixed = tour.pricing_mode === "fixed";
  const selectedPkg = packages.find((p: any) => p.id === selectedPackageId);

  const calculateTotal = () => {
    if (isFixed) {
      return (tour.adult_price || 0) * adults + (tour.child_price || 0) * children;
    }
    return selectedPkg?.price || 0;
  };

  const total = calculateTotal();

  const canBook = excursionDate && (schedules.length === 0 || selectedSchedule) &&
    (isFixed ? adults > 0 : !!selectedPackageId);

  const handleBookNow = () => {
    navigate("/tour-booking", {
      state: {
        tourId: tour.id,
        tourSlug: tour.slug,
        tourTitle: tour.title,
        tourImage: tour.main_image_url,
        destinationTitle: (tour as any).destinations?.title || "",
        pricingMode: tour.pricing_mode,
        adultPrice: tour.adult_price,
        childPrice: tour.child_price,
        adults,
        children,
        selectedPackageId: selectedPackageId || null,
        selectedPackageName: selectedPkg?.name || null,
        selectedPackagePrice: selectedPkg?.price || null,
        excursionDate: excursionDate?.toISOString(),
        schedule: selectedSchedule || null,
        totalPrice: total,
        duration: tour.duration,
      },
    });
  };

  return (
    <main className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-0 bg-tropical-dark overflow-hidden">
        <div className="absolute inset-0">
          <img src={tour.main_image_url || "/placeholder.svg"} alt={tour.title} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-tropical-dark/80 to-tropical-dark" />
        </div>
        <div className="absolute inset-0 bg-pattern-diagonal opacity-15" />
        <div className="container mx-auto px-4 relative z-10 py-12">
          <div className="flex items-center gap-2 text-hero-foreground/60 text-sm mb-4">
            <Link to="/tours" className="hover:text-secondary transition-colors">Tours</Link>
            <ChevronRight className="w-3 h-3" />
            {(tour as any).destinations?.title && (
              <>
                <Link to={`/destinations/${(tour as any).destinations?.slug}`} className="hover:text-secondary transition-colors">
                  {(tour as any).destinations.title}
                </Link>
                <ChevronRight className="w-3 h-3" />
              </>
            )}
            <span className="text-hero-foreground">{tour.title}</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-hero-foreground mb-4">{tour.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-hero-foreground/70 text-sm">
            {(tour as any).destinations?.title && (
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-secondary" />{(tour as any).destinations.title}</span>
            )}
            {tour.duration && <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-secondary" />{tour.duration}</span>}
            {tour.max_capacity && <span className="flex items-center gap-1"><Users className="w-4 h-4 text-secondary" />Max {tour.max_capacity} people</span>}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* LEFT: Content */}
            <div className="lg:col-span-2 space-y-10">
              {/* Gallery */}
              {allImages.length > 0 && (
                <div>
                  <div className="rounded-2xl overflow-hidden mb-3 aspect-video">
                    <img src={allImages[activeImage]} alt={tour.title} className="w-full h-full object-cover" />
                  </div>
                  {allImages.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {allImages.map((img, i) => (
                        <button key={i} onClick={() => setActiveImage(i)}
                          className={cn("rounded-lg overflow-hidden w-20 h-14 flex-shrink-0 border-2 transition-all", i === activeImage ? "border-primary" : "border-transparent opacity-60 hover:opacity-100")}>
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              {tour.short_description && (
                <p className="text-lg text-muted-foreground leading-relaxed">{tour.short_description}</p>
              )}
              {tour.long_description && (
                <div className="prose max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: tour.long_description.replace(/\n/g, '<br/>') }} />
              )}

              {/* Highlights */}
              {highlights.length > 0 && (
                <div>
                  <h2 className="font-display text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-secondary" /> Highlights
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {highlights.map((h, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-primary/5 border border-primary/10">
                        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span className="text-sm text-foreground">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* What to Expect */}
              {tour.what_to_expect && (
                <div>
                  <h2 className="font-display text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Compass className="w-5 h-5 text-primary" /> What to Expect
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">{tour.what_to_expect}</p>
                </div>
              )}

              {/* Includes / Not Includes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {includes.length > 0 && (
                  <div className="p-5 rounded-2xl bg-card border border-border">
                    <h3 className="font-display font-bold text-foreground mb-3 flex items-center gap-2">
                      <Check className="w-5 h-5 text-primary" /> What's Included
                    </h3>
                    <ul className="space-y-2">
                      {includes.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                          <Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {notIncludes.length > 0 && (
                  <div className="p-5 rounded-2xl bg-card border border-border">
                    <h3 className="font-display font-bold text-foreground mb-3 flex items-center gap-2">
                      <X className="w-5 h-5 text-destructive" /> Not Included
                    </h3>
                    <ul className="space-y-2">
                      {notIncludes.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <X className="w-3.5 h-3.5 text-destructive mt-0.5 shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Restrictions */}
              {restrictions.length > 0 && (
                <div className="p-5 rounded-2xl bg-destructive/5 border border-destructive/10">
                  <h3 className="font-display font-bold text-foreground mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-destructive" /> Restrictions
                  </h3>
                  <ul className="space-y-2">
                    {restrictions.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                        <AlertTriangle className="w-3.5 h-3.5 text-destructive mt-0.5 shrink-0" /> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What to Bring */}
              {whatToBring.length > 0 && (
                <div className="p-5 rounded-2xl bg-card border border-border">
                  <h3 className="font-display font-bold text-foreground mb-3 flex items-center gap-2">
                    <Backpack className="w-5 h-5 text-primary" /> What to Bring
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {whatToBring.map((item, i) => (
                      <span key={i} className="bg-primary/10 text-primary text-sm px-3 py-1.5 rounded-full">{item}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT: Booking Widget */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 p-6 rounded-2xl bg-card border border-border shadow-tropical space-y-5">
                {/* Price */}
                <div className="text-center pb-4 border-b border-border">
                  {isFixed ? (
                    <div>
                      <p className="text-muted-foreground text-sm mb-1">From</p>
                      <p className="text-4xl font-display font-bold text-primary">${tour.adult_price || 0}</p>
                      <p className="text-muted-foreground text-xs">per adult</p>
                      {(tour.child_price || 0) > 0 && (
                        <p className="text-muted-foreground text-xs mt-1">${tour.child_price} per child</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-muted-foreground text-sm mb-1">Packages available</p>
                      <p className="text-2xl font-display font-bold text-primary">
                        From ${packages.length > 0 ? Math.min(...packages.map((p: any) => p.price)) : 0}
                      </p>
                    </div>
                  )}
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Excursion Date *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !excursionDate && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {excursionDate ? format(excursionDate, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={excursionDate} onSelect={setExcursionDate} disabled={(d) => d < new Date()} initialFocus className="p-3 pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Schedule */}
                {schedules.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground">Schedule *</label>
                    <Select value={selectedSchedule} onValueChange={setSelectedSchedule}>
                      <SelectTrigger><SelectValue placeholder="Select time" /></SelectTrigger>
                      <SelectContent>
                        {schedules.map((s: string) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Fixed pricing: Adults/Children */}
                {isFixed && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Adults</p>
                        <p className="text-xs text-muted-foreground">${tour.adult_price || 0} each</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => setAdults(Math.max(1, adults - 1))}>
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-6 text-center font-bold text-foreground">{adults}</span>
                        <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => setAdults(adults + 1)}>
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    {(tour.child_price || 0) > 0 && (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-foreground">Children</p>
                          <p className="text-xs text-muted-foreground">${tour.child_price} each</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => setChildren(Math.max(0, children - 1))}>
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-6 text-center font-bold text-foreground">{children}</span>
                          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => setChildren(children + 1)}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Package pricing */}
                {!isFixed && packages.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground">Select Package *</label>
                    <div className="space-y-2">
                      {packages.map((pkg: any) => (
                        <button
                          key={pkg.id}
                          onClick={() => setSelectedPackageId(pkg.id)}
                          className={cn(
                            "w-full text-left p-4 rounded-xl border transition-all",
                            selectedPackageId === pkg.id
                              ? "border-primary bg-primary/5 shadow-sm"
                              : "border-border hover:border-primary/30"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-foreground text-sm">{pkg.name}</p>
                              {pkg.description && <p className="text-xs text-muted-foreground mt-0.5">{pkg.description}</p>}
                              {pkg.max_persons && <p className="text-xs text-muted-foreground">Up to {pkg.max_persons} persons</p>}
                            </div>
                            <p className="text-lg font-bold text-primary">${pkg.price}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="pt-4 border-t border-border">
                  <div className="flex items-end justify-between">
                    <p className="text-muted-foreground text-sm">Total</p>
                    <p className="text-3xl font-display font-bold text-primary">${total}</p>
                  </div>
                  {isFixed && (
                    <div className="text-xs text-muted-foreground text-right mt-1">
                      {adults} adult(s){children > 0 ? ` + ${children} child(ren)` : ""}
                    </div>
                  )}
                </div>

                {/* Book Button */}
                <Button
                  size="lg"
                  className="w-full bg-gradient-gold text-foreground hover:opacity-90 font-bold text-base rounded-full"
                  disabled={!canBook}
                  onClick={handleBookNow}
                >
                  Book Now
                </Button>

                <p className="text-center text-xs text-muted-foreground">Free cancellation up to 24h before</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TourDetailPage;
